import { useEffect, useState } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
const KEY = 'helpdesk.theme';

/** Light unless the person picked Dark or Device. */
function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

const media = () => window.matchMedia('(prefers-color-scheme: dark)');
const isDark = (choice: ThemeChoice) => choice === 'dark' || (choice === 'system' && media().matches);

function apply(choice: ThemeChoice) {
  const dark = isDark(choice);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#151d19' : '#2d6a4f');
}

/** Apply the saved theme before first paint (called from main.tsx). */
export function initTheme() {
  apply(read());
  media().addEventListener('change', () => {
    if (read() === 'system') apply('system');
  });
}

/** The choice, a setter, and whether the page is dark right now. */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(read);
  useEffect(() => {
    apply(choice);
    try {
      if (choice === 'light') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, choice);
    } catch {
      // Private browsing: the choice just won't persist.
    }
  }, [choice]);
  return [choice, setChoice, isDark(choice)] as const;
}
